import React from 'react';
import PropTypes from 'prop-types';
import stringUtils from '../../../../qti/utils/StringUtils';
import IntervalSaver from '../../../../services/IntervalSaver';
import { t } from '../../../../services/i18nService';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';
import { getQuestionContentIdForA11y } from './textEntryUtils';
import responseService from '../../../../services/ResponseService';

class TextEntryStringInput extends React.PureComponent {
    static propTypes = {
        callback: PropTypes.func.isRequired, // callback(responseIdentifier, [simpleChoice identifiers])
        defaultValue: PropTypes.string,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        handleInteractionReady: PropTypes.func.isRequired,
        isReadOnly: PropTypes.bool,
        maxLength: PropTypes.number,
        size: PropTypes.number,
        orderNumber: PropTypes.string,
        questionModel: PropTypes.object,
        services: PropTypes.object,
        isActive: PropTypes.bool,
        isFlagged: PropTypes.bool,
        onChangeCallback: PropTypes.func,
        expandableInputStyles: PropTypes.object,
        keyword: PropTypes.string,
    };

    constructor(props) {
        super(props);
        this.instanceId = stringUtils.getUID();
        this.state = {
            instanceId: stringUtils.getUID(),
            isFocused: false,
        };
        this.smartSaver = new IntervalSaver(props.defaultValue, (value) => {
            props.callback(props.model.identifier, [value]);
        });
    }

    componentDidMount() {
        this.props.handleInteractionReady(true);
        if (this.props.questionModel) {
            responseService.subscribeToItemResultUpdates(this.props.questionModel.contentRevisionId, this.flush);
        }
    }

    componentWillUnmount() {
        if (this.props.questionModel) {
            responseService.unsubscribeFromItemResultUpdates(this.props.questionModel.contentRevisionId, this.flush);
        }
        this.smartSaver.saveAndStop();
    }

    flush = () => {
        if (this.inputElement && this.smartSaver.currentValue !== this.smartSaver.lastStoredValue) {
            this.smartSaver.setValue(this.inputElement.value);
        }
    }

    onChange = (e) => {
        const { onChangeCallback, model } = this.props;
        const value = e.currentTarget.value;

        // Use delayed save if a11y screen reader support is enabled
        if (a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported()) {
            // Queue value without immediate save - save happens on blur
            this.smartSaver.currentValue = value;
            this.smartSaver.lastSetValueTS = new Date().getTime();
        } else {
            // Default behavior - save immediately
            this.smartSaver.setValue(value);
        }

        if (model.expandAutomatically && onChangeCallback) {
            onChangeCallback(this.inputElement.value);
        }
    };

    setValue(value) {
        this.inputElement.value = value;
    }

    onBlur = (e) => {
        const value = e.currentTarget.value;

        // Only save on blur if a11y screen reader support is enabled
        if (a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported()) {
            this.smartSaver.setValue(value);
        }
    };

    navigateToScorableItem = () => {
        const { services, model, questionModel } = this.props;

        // Guard: Only track focused state if a11y screen reader support is enabled
        if (a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported()) {
            this.setState({ isFocused: true });
        }

        services.scorableItemNumberingService.navigateToScorableItem(
            questionModel.contentRevisionId,
            model.identifier,
            undefined,
            true
        );
    };

    onBlurHandler = (e) => {
        if (a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported()) {
            this.setState({ isFocused: false });
        }

        this.onBlur(e);
    };

    render() {
        const {
            defaultValue,
            isReadOnly,
            maxLength,
            model,
            size,
            orderNumber,
            questionModel,
            isActive,
            isFlagged,
            expandableInputStyles,
            keyword,
        } = this.props;
        const isActiveClass = isActive ? ' active' : '';
        const isFlaggedClass = isFlagged ? ' flagged' : '';
        const interactionTypeClass = (model.interactionType === 'graphic') ? ' graphic' : '';
        const className = `textEntryInteractionValue${interactionTypeClass}${isActiveClass}${isFlaggedClass}`;
        const style = model.expandAutomatically && expandableInputStyles ? expandableInputStyles : null;
        const isA11yIIBScreenReaderSupported = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported();
        const defaultAriaLabel = t('interactions.inputEntry.inputFieldAriaLabel');
        const customAriaLabel = model?.customLabelText || defaultAriaLabel;
        const a11yAriaLabel = model?.customLabelText || t('interactions.inputEntry.textInputFieldAriaLabel');
        let labelMessage;
        if (isA11yIIBScreenReaderSupported) {
            labelMessage = this.state.isFocused ? a11yAriaLabel : undefined;
        } else {
            labelMessage = customAriaLabel;
        }

        const placeholderText = isA11yIIBScreenReaderSupported ? undefined : orderNumber;
        const roleValue = isA11yIIBScreenReaderSupported ? 'textbox' : undefined;
        const isA11yV5Enabled = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(
            A11yForIIBScreenReaderIteration.v5
        );
        const labelAriaHidden = (isA11yIIBScreenReaderSupported && !isA11yV5Enabled) ? 'true' : undefined;
        const questionContentId = isA11yV5Enabled ? getQuestionContentIdForA11y(questionModel, model) : undefined;
        const ariaLabelledBy = isA11yV5Enabled ? questionContentId : undefined;

        const handler = (event) => {
            event.preventDefault();
            return false;
        };
        return (
            <label htmlFor={this.instanceId} aria-hidden={labelAriaHidden}>
                <input
                    id={this.instanceId}
                    autoComplete="off"
                    className={className}
                    defaultValue={defaultValue}
                    disabled={isReadOnly}
                    maxLength={maxLength}
                    name={this.state.instanceId}
                    onChange={this.onChange}
                    onBlur={this.onBlurHandler}
                    onFocus={this.navigateToScorableItem}
                    onDrop={handler}
                    ref={(element) => {
                        this.inputElement = element;
                    }}
                    spellCheck="false"
                    size={size}
                    type="text"
                    placeholder={placeholderText}
                    style={style}
                    keyword={keyword}
                    aria-label={labelMessage}
                    aria-labelledby={ariaLabelledBy}
                    tabIndex={0}
                    role={roleValue}
                />
            </label>
        );
    }
}

export default TextEntryStringInput;
